import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Smile } from "lucide-react";
import axiosInstance from "../api/axios";
import useClickOutside from "../hooks/useClickOutside";
import { REACTIONS } from "../constants/reactions";

// Space kept between the popover and the viewport edges, and between the
// popover and its trigger.
const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 8;

// Where to put a popover of `menuWidth` x `menuHeight` next to a trigger
// (`trigger` is its viewport rect) in a `viewportWidth` x `viewportHeight`
// viewport: above it when it fits, else below it when that fits, else on the
// roomier side with its height capped (`maxHeight`, the list then scrolls).
// Right-aligned with the trigger, then shifted to stay inside the viewport.
export function computePickerPosition({ trigger, menuWidth, menuHeight, viewportWidth, viewportHeight }) {
  const spaceAbove = trigger.top - TRIGGER_GAP - VIEWPORT_MARGIN;
  const spaceBelow = viewportHeight - trigger.bottom - TRIGGER_GAP - VIEWPORT_MARGIN;

  let placement;
  let maxHeight = null;
  if (menuHeight <= spaceAbove) {
    placement = "top";
  } else if (menuHeight <= spaceBelow) {
    placement = "bottom";
  } else {
    placement = spaceAbove >= spaceBelow ? "top" : "bottom";
    maxHeight = Math.max(0, placement === "top" ? spaceAbove : spaceBelow);
  }
  const height = maxHeight ?? menuHeight;
  const top = placement === "top" ? trigger.top - TRIGGER_GAP - height : trigger.bottom + TRIGGER_GAP;

  const maxLeft = viewportWidth - VIEWPORT_MARGIN - menuWidth;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(trigger.right - menuWidth, maxLeft));

  return { placement, top, left, maxHeight };
}

// Reaction trigger button + popover. For a post it owns the POST request
// itself so PostCard/PostStatic don't duplicate reaction request/response
// handling — each just hands over postId/token/myReaction and gets called back
// with the server's authoritative { my_reaction, reaction_counts } to merge
// into its own post state (same pattern each already uses for likes/comments).
//
// Another target (an event occurrence) passes `onSelect(reactionType)`
// instead: it makes the request and resolves to { my_reaction,
// reaction_counts }, or rejects (the caller reports the error); onReacted is
// still called on success.
//
// The popover is portaled to <body> with fixed positioning measured from the
// trigger (computePickerPosition), so a scrolling ancestor (the post and event
// focus views) can't clip it and it stays inside the viewport. It follows the
// trigger on scroll and resize, and closes once the trigger scrolls out of view.
export default function ReactionPickerButton({
  postId,
  token,
  myReaction,
  onReacted,
  onSelect,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  useClickOutside([menuRef, buttonRef], close);

  // Measures and places the open popover. Written straight to its style so
  // repositioning on every scroll frame doesn't re-render.
  const place = useCallback(() => {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button || !menu) return;
    const trigger = button.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    if (trigger.bottom < 0 || trigger.top > viewportHeight) {
      setOpen(false);
      return;
    }
    // Measure the uncapped size.
    menu.style.maxHeight = "";
    const size = menu.getBoundingClientRect();
    const { placement, top, left, maxHeight } = computePickerPosition({
      trigger,
      menuWidth: size.width,
      menuHeight: size.height,
      viewportWidth,
      viewportHeight,
    });
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.maxHeight = maxHeight == null ? "" : `${maxHeight}px`;
    menu.dataset.placement = placement;
    menu.dataset.constrained = maxHeight == null ? "false" : "true";
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // While open: follow the trigger (any scrolling ancestor — capture phase),
  // and Escape closes, returning focus to the trigger.
  useEffect(() => {
    if (!open) return undefined;
    let frame = null;
    const schedule = () => {
      if (frame == null) {
        frame = window.requestAnimationFrame(() => {
          frame = null;
          place();
        });
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        // Only the popover: not a surrounding modal (ModalBackdrop listens on window).
        event.stopPropagation();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, { capture: true });
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, place]);

  // The portaled popover sits at the end of <body>, outside the page's tab
  // order: move focus into it (current reaction, else the first).
  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    (menu?.querySelector('[aria-checked="true"]') || menu?.querySelector("button"))?.focus({
      preventScroll: true,
    });
  }, [open]);

  const handleSelect = async (reactionType) => {
    if (pending) return;

    setOpen(false);
    setPending(true);
    try {
      if (onSelect) {
        onReacted(await onSelect(reactionType));
        return;
      }
      const { data } = await axiosInstance.post(
        `/posts/${postId}/reaction/`,
        { reaction_type: reactionType },
        { headers: { Authorization: `Token ${token}` } }
      );
      // Backend response is the full authoritative state — no client-side
      // increment/decrement math, so there's no way to double-count or
      // under-decrement when switching reactions.
      onReacted({ my_reaction: data.my_reaction, reaction_counts: data.reaction_counts });
    } catch (err) {
      console.error("Failed to react:", err);
      // Nothing was optimistically changed, so existing state is already correct.
    } finally {
      setPending(false);
    }
  };

  const activeReaction = REACTIONS.find((r) => r.type === myReaction);
  const isDisabled = disabled || pending;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !isDisabled && setOpen((prev) => !prev)}
        disabled={isDisabled}
        aria-label={activeReaction ? "Change reaction" : "Add reaction"}
        aria-haspopup="true"
        aria-expanded={open}
        className={`flex items-center ${
          isDisabled ? "text-gray-400 cursor-not-allowed" : "text-gray-600 hover:text-blue-500"
        }`}
      >
        <Smile />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            onKeyDown={(event) => {
              // Leaving the popover by keyboard returns to the trigger, so Tab
              // continues from there in the page's order.
              if (event.key === "Tab") {
                setOpen(false);
                buttonRef.current?.focus();
              }
            }}
            className="fixed top-0 left-0 z-[70] flex max-w-[calc(100vw-16px)] flex-wrap items-center gap-1 overflow-y-auto overscroll-contain rounded-3xl border border-gray-200 bg-white px-2 py-1.5 shadow-md"
          >
            {REACTIONS.map((r) => (
              <button
                key={r.type}
                type="button"
                role="menuitemradio"
                aria-checked={myReaction === r.type}
                aria-label={`React with ${r.label}`}
                onClick={() => handleSelect(r.type)}
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-lg leading-none transition-transform hover:scale-110 hover:bg-gray-100 ${
                  myReaction === r.type ? "bg-blue-50 ring-1 ring-blue-300" : ""
                }`}
              >
                {r.emoji}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
